"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { CaretLeftIcon, CaretRightIcon, XIcon } from "@phosphor-icons/react";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import type { ProductImage } from "@/types";

/** 핀치 줌 배율 범위 / 두 번 탭 배율 / 두 번 탭 판정 간격. */
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 280;

/** Hero 전환 시간 / 뷰어 페이드 시간. */
const HERO_MS = 320;
const FADE_MS = 200;
const HERO_EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";

/**
 * 상세 화면에서 누른 사진 — Hero 전환의 출발점.
 * `src` 는 그 화면에서 이미 받은 파일(img.currentSrc)이라 날아가는 동안 새로 받지 않는다.
 */
export interface ViewerOrigin {
  rect: DOMRect;
  src: string;
  /** 원본 가로/세로 비율(naturalWidth / naturalHeight) */
  ratio: number;
}

interface ProductImageViewerProps {
  images: ProductImage[];
  productName: string;
  open: boolean;
  startIndex: number;
  origin?: ViewerOrigin | null;
  onClose: () => void;
}

type Rect = { left: number; top: number; width: number; height: number };

/** box 안에 비율 ratio 인 사진을 object-contain 으로 넣었을 때 실제로 보이는 영역. */
function containRect(box: Rect, ratio: number): Rect {
  const boxRatio = box.width / box.height;
  const width = boxRatio > ratio ? box.height * ratio : box.width;
  const height = boxRatio > ratio ? box.height : box.width / ratio;
  return {
    left: box.left + (box.width - width) / 2,
    top: box.top + (box.height - height) / 2,
    width,
    height,
  };
}

/**
 * 출발(상세의 정사각 crop) → 도착(뷰어의 전체 사진) 변환.
 * 도착 위치에 둔 요소를 scale·translate 로 출발 크기에 덮고, clip-path 로 출발의 crop 만 보이게 한다.
 * 이 둘을 none 으로 풀면 crop 이 풀리며 사진이 제자리로 커진다.
 */
function heroFrom(origin: Rect, target: Rect) {
  const scale = Math.max(
    origin.width / target.width,
    origin.height / target.height
  );
  const tx = origin.left + origin.width / 2 - (target.left + target.width / 2);
  const ty = origin.top + origin.height / 2 - (target.top + target.height / 2);
  const insetX = Math.max(0, (target.width - origin.width / scale) / 2);
  const insetY = Math.max(0, (target.height - origin.height / scale) / 2);
  return {
    transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
    clipPath: `inset(${insetY}px ${insetX}px)`,
  };
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * 상품 사진 뷰어(토스쇼핑식).
 * - 모바일: 꽉 찬 어두운 시트, 옆으로 넘기기(scroll-snap), 핀치 줌·두 번 탭 확대, 하단 썸네일.
 * - 데스크톱: 어두운 오버레이 + 가운데 적당한 크기(최대 880×600, 화면 높이 62%) + 양옆 화살표 + 하단 썸네일.
 *   ←/→ 로 넘기고 Esc·빈 곳 클릭으로 닫는다.
 * - 열고 닫을 때 상세에서 누른 사진이 뷰어 자리로 커지는 Hero 전환(FLIP).
 *   닫을 때는 처음 연 사진을 보고 있을 때만 제자리로 돌아가고, 아니면 페이드아웃.
 */
export default function ProductImageViewer({
  images,
  productName,
  open,
  startIndex,
  origin,
  onClose,
}: ProductImageViewerProps) {
  const isMobile = useIsMobile();

  // 열려 있는 동안 뒤 페이지 스크롤 잠금
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // 사용자가 누른 뒤에만 열리므로 서버 렌더에서는 항상 닫혀 있다.
  if (!open || images.length === 0 || typeof document === "undefined")
    return null;

  return createPortal(
    <ViewerShell
      key={`${startIndex}-${isMobile ? "m" : "d"}`}
      images={images}
      productName={productName}
      startIndex={startIndex}
      origin={origin ?? null}
      isMobile={isMobile}
      onClose={onClose}
    />,
    document.body
  );
}

type Phase = "enter" | "open" | "exit";

/** 뷰어 본문 + Hero 전환 레이어. */
function ViewerShell({
  images,
  productName,
  startIndex,
  origin,
  isMobile,
  onClose,
}: {
  images: ProductImage[];
  productName: string;
  startIndex: number;
  origin: ViewerOrigin | null;
  isMobile: boolean;
  onClose: () => void;
}) {
  const targetRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [useHero] = useState(() => !!origin && !prefersReducedMotion());
  const [phase, setPhase] = useState<Phase>("enter");
  const [heroRect, setHeroRect] = useState<Rect | null>(null);
  const closing = useRef(false);

  const measureTarget = useCallback((): Rect | null => {
    const box = targetRef.current?.getBoundingClientRect();
    if (!box || !origin) return null;
    return containRect(box, origin.ratio);
  }, [origin]);

  // 열기: 도착 위치를 재고 출발 모양으로 덮은 뒤 다음 프레임에 풀어 준다.
  // 첫 프레임은 투명(opacity 0)으로 그려 두고, 다음 프레임에 재서 시작한다.
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      const target = useHero ? measureTarget() : null;
      if (target) setHeroRect(target);
      else setPhase("open");
    });
    return () => cancelAnimationFrame(id);
  }, [useHero, measureTarget]);

  useLayoutEffect(() => {
    const el = heroRef.current;
    if (!el || !heroRect || !origin || phase !== "enter") return;
    const from = heroFrom(origin.rect, heroRect);
    el.style.transition = "none";
    el.style.transform = from.transform;
    el.style.clipPath = from.clipPath;
    el.getBoundingClientRect(); // 시작 모양을 확정시킨 뒤 전환 시작
    el.style.transition = `transform ${HERO_MS}ms ${HERO_EASE}, clip-path ${HERO_MS}ms ${HERO_EASE}`;
    el.style.transform = "none";
    el.style.clipPath = "inset(0px 0px)";
    const done = window.setTimeout(() => setPhase("open"), HERO_MS);
    return () => window.clearTimeout(done);
  }, [heroRect, origin, phase]);

  const requestClose = useCallback(
    (currentIndex: number) => {
      if (closing.current) return;
      closing.current = true;
      const backToOrigin = useHero && currentIndex === startIndex && origin;
      const target = backToOrigin ? measureTarget() : null;
      if (backToOrigin && target) {
        setHeroRect(target);
        setPhase("exit");
        requestAnimationFrame(() => {
          const el = heroRef.current;
          if (!el) return;
          const to = heroFrom(origin.rect, target);
          el.style.transition = `transform ${HERO_MS}ms ${HERO_EASE}, clip-path ${HERO_MS}ms ${HERO_EASE}`;
          el.style.transform = to.transform;
          el.style.clipPath = to.clipPath;
        });
        window.setTimeout(onClose, HERO_MS);
      } else {
        setPhase("exit");
        window.setTimeout(onClose, FADE_MS);
      }
    },
    [useHero, startIndex, origin, measureTarget, onClose]
  );

  const showHero = useHero && heroRect && phase !== "open";
  const backdropVisible = phase === "open" || (phase === "enter" && !!heroRect);
  // 전환 중에는 진짜 사진 대신 날아가는 사진만 보이게 한다.
  const hideStartImage = showHero;

  const bodyProps = {
    images,
    productName,
    startIndex,
    hideStartImage: !!hideStartImage,
    placeholderSrc: origin?.src,
    targetRef,
    onRequestClose: requestClose,
  };

  return (
    <>
      <div
        className="fixed inset-0 z-60 transition-opacity ease-out motion-reduce:transition-none"
        style={{
          opacity: phase === "exit" ? 0 : backdropVisible ? 1 : 0,
          transitionDuration: `${
            phase === "exit" && !showHero ? FADE_MS : HERO_MS
          }ms`,
        }}
      >
        {isMobile ? (
          <MobileViewer {...bodyProps} />
        ) : (
          <DesktopViewer {...bodyProps} />
        )}
      </div>

      {showHero && origin && (
        <div
          ref={heroRef}
          aria-hidden
          className="pointer-events-none fixed z-61 overflow-hidden will-change-transform"
          style={{
            left: heroRect.left,
            top: heroRect.top,
            width: heroRect.width,
            height: heroRect.height,
          }}
        >
          {/* 상세에서 이미 받은 파일 그대로 — 새 요청 없음 */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={origin.src}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
          />
        </div>
      )}
    </>
  );
}

type ViewerBodyProps = {
  images: ProductImage[];
  productName: string;
  startIndex: number;
  /** Hero 전환 중에는 시작 사진을 숨긴다(날아가는 사진과 겹치지 않게). */
  hideStartImage: boolean;
  /** 시작 사진 밑에 먼저 깔 이미 받은 파일 — 큰 파일을 받는 동안 빈 화면·스피너가 안 보이게. */
  placeholderSrc?: string;
  /** Hero 도착 위치(시작 사진이 들어갈 상자) */
  targetRef: RefObject<HTMLDivElement | null>;
  onRequestClose: (currentIndex: number) => void;
};

/**
 * 뷰어 사진 한 장: 밝은 blur placeholder 대신 어두운 배경 위 스피너, 다 받으면 살짝 페이드인.
 * placeholderSrc 가 있으면(상세에서 이미 받은 파일) 그걸 먼저 깔아 스피너도 안 보이게 한다.
 */
function ViewerImage({
  src,
  alt,
  sizes,
  priority,
  placeholderSrc,
  className,
}: {
  src: string;
  alt: string;
  sizes: string;
  priority?: boolean;
  placeholderSrc?: string;
  className?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      {!loaded &&
        (placeholderSrc ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={placeholderSrc}
            alt=""
            aria-hidden
            className={cn(
              "absolute inset-0 h-full w-full object-contain",
              className
            )}
            draggable={false}
          />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/15 border-t-white/60" />
          </span>
        ))}
      <OptimizedImage
        src={src}
        alt={alt}
        fill
        priority={priority}
        sizes={sizes}
        enablePlaceholder={false}
        onLoad={() => setLoaded(true)}
        className={cn(
          "object-contain transition-opacity duration-150 select-none",
          loaded ? "opacity-100" : "opacity-0",
          className
        )}
        draggable={false}
      />
    </>
  );
}

function Thumbnails({
  images,
  productName,
  index,
  onSelect,
  size,
  className,
}: {
  images: ProductImage[];
  productName: string;
  index: number;
  onSelect: (i: number) => void;
  size: "sm" | "md";
  className?: string;
}) {
  const t = useTranslations("product.gallery");
  const listRef = useRef<HTMLDivElement>(null);

  // 지금 사진 썸네일이 항상 보이게 줄을 따라 움직인다.
  useEffect(() => {
    const list = listRef.current;
    const item = list?.children[index] as HTMLElement | undefined;
    if (!list || !item) return;
    list.scrollTo({
      left: item.offsetLeft - list.clientWidth / 2 + item.clientWidth / 2,
      behavior: "smooth",
    });
  }, [index]);

  return (
    <div
      ref={listRef}
      className={cn(
        "flex shrink-0 gap-2 overflow-x-auto px-4 scrollbar-hide",
        className
      )}
    >
      {images.map((img, i) => (
        <button
          key={img.id}
          type="button"
          onClick={() => onSelect(i)}
          aria-label={t("photoLabel", { index: i + 1 })}
          aria-current={i === index}
          className={cn(
            "relative shrink-0 cursor-pointer overflow-hidden rounded-[10px] border-2 bg-white/10 transition-[opacity,border-color] duration-150",
            size === "sm" ? "h-14 w-14" : "h-16 w-16",
            i === index
              ? "border-white opacity-100"
              : "border-transparent opacity-50 hover:opacity-80"
          )}
        >
          {/* 썸네일은 blur placeholder 없이 — 어두운 칸에 밝은 회색이 번쩍이지 않게 */}
          <OptimizedImage
            src={img.image_url}
            alt={`${productName} ${i + 1}`}
            fill
            sizes="64px"
            enablePlaceholder={false}
            className="object-cover"
          />
        </button>
      ))}
    </div>
  );
}

function MobileViewer({
  images,
  productName,
  startIndex,
  hideStartImage,
  placeholderSrc,
  targetRef,
  onRequestClose,
}: ViewerBodyProps) {
  const t = useTranslations("product.gallery");
  const stageRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(startIndex);
  const [zoomed, setZoomed] = useState(false);
  const programmatic = useRef(false);

  // 처음 열 때 누른 사진 위치로 바로(애니메이션 없이) 이동
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (stage) stage.scrollLeft = startIndex * stage.clientWidth;
  }, [startIndex]);

  const goTo = (i: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    programmatic.current = true;
    stage.scrollTo({ left: i * stage.clientWidth, behavior: "smooth" });
    setIndex(i);
    window.setTimeout(() => {
      programmatic.current = false;
    }, 400);
  };

  const onScroll = () => {
    const stage = stageRef.current;
    if (!stage || programmatic.current) return;
    const i = Math.round(stage.scrollLeft / stage.clientWidth);
    if (i !== index) setIndex(i);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onRequestClose(index);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onRequestClose, index]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={productName}
      className="flex h-full flex-col bg-[#1b1d22] text-white"
    >
      <div className="box-content flex h-13 shrink-0 items-center justify-between px-2 pt-[var(--loc-safe-area-inset-top)]">
        <button
          type="button"
          onClick={() => onRequestClose(index)}
          aria-label={t("close")}
          className="flex h-10 w-10 items-center justify-center rounded-full active:bg-white/10"
        >
          <CaretLeftIcon className="h-6 w-6" />
        </button>
        <span className="text-[13px] tabular-nums text-white/70">
          {index + 1} / {images.length}
        </span>
        <span className="w-10" />
      </div>

      {/* 확대 중에는 옆 넘기기를 막아 한 손가락 이동이 사진 이동이 되게 한다.
          stage 한 칸 = 사진 상자라 Hero 도착 위치로 stage 를 잰다. */}
      <div
        ref={(el) => {
          stageRef.current = el;
          targetRef.current = el;
        }}
        onScroll={onScroll}
        className={cn(
          "flex min-h-0 flex-1 snap-x snap-mandatory overscroll-contain scrollbar-hide",
          zoomed ? "overflow-hidden" : "overflow-x-auto"
        )}
      >
        {images.map((img, i) => (
          <ZoomableSlide
            key={img.id}
            src={img.image_url}
            alt={`${productName} ${i + 1}`}
            active={i === index}
            priority={i === startIndex}
            hidden={hideStartImage && i === startIndex}
            placeholderSrc={i === startIndex ? placeholderSrc : undefined}
            onZoomChange={setZoomed}
          />
        ))}
      </div>

      <Thumbnails
        images={images}
        productName={productName}
        index={index}
        onSelect={goTo}
        size="sm"
        className="pt-3 pb-[max(1.25rem,var(--loc-safe-area-inset-bottom))]"
      />
    </div>
  );
}

/**
 * 핀치 줌 슬라이드. 브라우저 확대는 viewport maximumScale=1 로 꺼져 있어 직접 처리한다.
 * - 두 손가락: 확대/축소(1~4배), 손가락 중점 이동만큼 함께 이동
 * - 확대 중 한 손가락: 사진 이동(가장자리 밖으로는 안 나감)
 * - 두 번 탭: 2.5배 ↔ 원래 크기
 * 터치 리스너는 preventDefault 가 필요해서 passive:false 로 직접 단다.
 */
function ZoomableSlide({
  src,
  alt,
  active,
  priority,
  hidden,
  placeholderSrc,
  onZoomChange,
}: {
  src: string;
  alt: string;
  active: boolean;
  priority: boolean;
  hidden: boolean;
  placeholderSrc?: string;
  onZoomChange: (zoomed: boolean) => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const view = useRef({ scale: 1, x: 0, y: 0 });

  const apply = useCallback((animate: boolean) => {
    const layer = layerRef.current;
    if (!layer) return;
    const { scale, x, y } = view.current;
    layer.style.transition = animate ? "transform 0.2s ease-out" : "none";
    layer.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  }, []);

  const clampPan = useCallback(() => {
    const box = boxRef.current;
    if (!box) return;
    const v = view.current;
    const maxX = ((v.scale - 1) * box.clientWidth) / 2;
    const maxY = ((v.scale - 1) * box.clientHeight) / 2;
    v.x = Math.max(-maxX, Math.min(maxX, v.x));
    v.y = Math.max(-maxY, Math.min(maxY, v.y));
  }, []);

  // 다른 사진으로 넘어가면 확대를 풀어 둔다.
  useEffect(() => {
    if (active) return;
    view.current = { scale: 1, x: 0, y: 0 };
    apply(false);
  }, [active, apply]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    let pinch: {
      dist: number;
      scale: number;
      mx: number;
      my: number;
      x: number;
      y: number;
    } | null = null;
    let pan: { sx: number; sy: number; x: number; y: number } | null = null;
    let lastTap = 0;
    let moved = false;

    const dist = (a: Touch, b: Touch) =>
      Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const onStart = (e: TouchEvent) => {
      const v = view.current;
      moved = false;
      if (e.touches.length === 2) {
        const [a, b] = [e.touches[0], e.touches[1]];
        pinch = {
          dist: dist(a, b),
          scale: v.scale,
          mx: (a.clientX + b.clientX) / 2,
          my: (a.clientY + b.clientY) / 2,
          x: v.x,
          y: v.y,
        };
        pan = null;
      } else if (e.touches.length === 1 && v.scale > 1) {
        pan = {
          sx: e.touches[0].clientX,
          sy: e.touches[0].clientY,
          x: v.x,
          y: v.y,
        };
      }
    };

    const onMove = (e: TouchEvent) => {
      const v = view.current;
      if (pinch && e.touches.length === 2) {
        e.preventDefault();
        moved = true;
        const [a, b] = [e.touches[0], e.touches[1]];
        v.scale = Math.max(
          MIN_SCALE * 0.9,
          Math.min(MAX_SCALE, (pinch.scale * dist(a, b)) / pinch.dist)
        );
        v.x = pinch.x + ((a.clientX + b.clientX) / 2 - pinch.mx);
        v.y = pinch.y + ((a.clientY + b.clientY) / 2 - pinch.my);
        apply(false);
      } else if (pan && e.touches.length === 1) {
        e.preventDefault();
        moved = true;
        v.x = pan.x + (e.touches[0].clientX - pan.sx);
        v.y = pan.y + (e.touches[0].clientY - pan.sy);
        clampPan();
        apply(false);
      }
    };

    const onEnd = (e: TouchEvent) => {
      const v = view.current;
      if (pinch && e.touches.length < 2) {
        pinch = null;
        if (v.scale <= 1.02) view.current = { scale: 1, x: 0, y: 0 };
        else clampPan();
        apply(true);
        onZoomChange(view.current.scale > 1);
        return;
      }
      if (pan && e.touches.length === 0) pan = null;

      // 두 번 탭: 탭한 지점을 기준으로 확대 / 확대 중이면 원래대로
      if (!moved && e.touches.length === 0 && e.changedTouches.length === 1) {
        const now = Date.now();
        if (now - lastTap < DOUBLE_TAP_MS) {
          lastTap = 0;
          if (v.scale > 1) {
            view.current = { scale: 1, x: 0, y: 0 };
          } else {
            const rect = box.getBoundingClientRect();
            const tx =
              e.changedTouches[0].clientX - (rect.left + rect.width / 2);
            const ty =
              e.changedTouches[0].clientY - (rect.top + rect.height / 2);
            view.current = {
              scale: DOUBLE_TAP_SCALE,
              x: -tx * (DOUBLE_TAP_SCALE - 1),
              y: -ty * (DOUBLE_TAP_SCALE - 1),
            };
            clampPan();
          }
          apply(true);
          onZoomChange(view.current.scale > 1);
        } else {
          lastTap = now;
        }
      }
    };

    box.addEventListener("touchstart", onStart, { passive: true });
    box.addEventListener("touchmove", onMove, { passive: false });
    box.addEventListener("touchend", onEnd);
    box.addEventListener("touchcancel", onEnd);
    return () => {
      box.removeEventListener("touchstart", onStart);
      box.removeEventListener("touchmove", onMove);
      box.removeEventListener("touchend", onEnd);
      box.removeEventListener("touchcancel", onEnd);
    };
  }, [apply, clampPan, onZoomChange]);

  return (
    <div
      ref={boxRef}
      className="relative h-full w-full shrink-0 snap-center overflow-hidden"
    >
      <div
        ref={layerRef}
        className={cn(
          "absolute inset-0 will-change-transform",
          hidden && "invisible"
        )}
      >
        <ViewerImage
          src={src}
          alt={alt}
          sizes="100vw"
          priority={priority}
          placeholderSrc={placeholderSrc}
        />
      </div>
    </div>
  );
}

function DesktopViewer({
  images,
  productName,
  startIndex,
  hideStartImage,
  placeholderSrc,
  targetRef,
  onRequestClose,
}: ViewerBodyProps) {
  const t = useTranslations("product.gallery");
  const [index, setIndex] = useState(startIndex);
  const last = images.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onRequestClose(index);
      else if (e.key === "ArrowLeft") setIndex((i) => Math.max(0, i - 1));
      else if (e.key === "ArrowRight") setIndex((i) => Math.min(last, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onRequestClose, index, last]);

  const navClass =
    "absolute top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20 disabled:cursor-default disabled:opacity-25 disabled:hover:bg-white/10";

  const closeOnBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onRequestClose(index);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={productName}
      className="flex h-full flex-col bg-[rgba(17,18,22,0.92)] text-white"
    >
      <div className="flex h-14 shrink-0 items-center justify-between px-4">
        <span className="text-[13px] tabular-nums text-white/70">
          {index + 1} / {images.length}
        </span>
        <button
          type="button"
          onClick={() => onRequestClose(index)}
          aria-label={t("close")}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full hover:bg-white/10"
        >
          <XIcon className="h-6 w-6" />
        </button>
      </div>

      {/* 사진 + 썸네일을 한 묶음으로 세로 가운데에 둔다(썸네일이 화면 바닥에 붙지 않게).
          아래 여백(pb-14)은 위 헤더(h-14)와 맞춰 시각적 중앙을 잡는다.
          빈 곳(사진·버튼 밖)을 누르면 닫힌다. */}
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 pb-14"
        onClick={closeOnBackdrop}
      >
        <div
          className="relative flex w-full items-center justify-center px-20"
          onClick={closeOnBackdrop}
        >
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
            aria-label={t("prev")}
            className={cn(navClass, "left-4")}
          >
            <CaretLeftIcon className="h-5 w-5" />
          </button>

          <div
            ref={targetRef}
            className={cn(
              "relative h-[min(600px,62vh)] w-[min(880px,100%)]",
              hideStartImage && index === startIndex && "invisible"
            )}
          >
            <ViewerImage
              key={images[index].id}
              src={images[index].image_url}
              alt={`${productName} ${index + 1}`}
              sizes="880px"
              priority
              placeholderSrc={index === startIndex ? placeholderSrc : undefined}
              className="rounded-md"
            />
          </div>

          <button
            type="button"
            onClick={() => setIndex((i) => Math.min(last, i + 1))}
            disabled={index === last}
            aria-label={t("next")}
            className={cn(navClass, "right-4")}
          >
            <CaretRightIcon className="h-5 w-5" />
          </button>
        </div>

        <Thumbnails
          images={images}
          productName={productName}
          index={index}
          onSelect={setIndex}
          size="md"
          className="w-full max-w-[min(880px,100%)] justify-center-safe py-1"
        />
      </div>
    </div>
  );
}
